import type { FC } from 'react';

import { useMemo, useCallback } from 'react';

import { Box, Button, Typography, useTheme } from '@mui/material';

import { toast } from 'src/components/snackbar';

import { getLiveChatScript } from '../utils/getLiveChatScript';

// ==================== Types ====================
interface ScriptData {
  tenantId: string;
  websiteToken: string;
  welcomeTitle: string;
  welcomeTagline: string;
  widgetColor: string;
}

interface CopyScriptPageProps {
  scriptData: ScriptData;
  redirectToRoot: () => void;
  t: (key: string) => string;
  isEdit?: boolean;
}

// ==================== Constants ====================
const SCRIPT_CONFIG = {
  sdkPath: '/sdk/chat-agent.js',
  fontSize: '0.75rem',
} as const;

// ==================== Component ====================
export const CopyScriptPage: FC<CopyScriptPageProps> = ({
  scriptData,
  redirectToRoot,
  t,
  isEdit = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const codeString = useMemo(
    () => getLiveChatScript(SCRIPT_CONFIG.sdkPath, scriptData),
    [scriptData]
  );

  const handleCopyScript = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      toast.success(t('SCRIPT_COPIED_SUCCESS'));
    } catch (error) {
      toast.error(t('SCRIPT_COPY_FAILED'));
    }
  }, [codeString, t]);

  if (!scriptData) return null;

  return (
    <Box
      sx={{
        height: '90%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 1,
        p: { xs: 2, md: 8 },
      }}
    >
      <Box sx={{ width: { xs: '100%', md: '60%' }, textAlign: 'center', my: { xs: 2, md: 5 } }}>
        <Typography variant="h3" sx={{ mb: 3 }}>
          {isEdit ? t('CHANNEL_UPDATED_SUCCESSFULLY') : t('CHANNEL_CREATED_SUCCESSFULLY')}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {t('LIVE_CHAT_MESSAGE')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowX: 'auto', width: '100%', maxWidth: 800 }}>
        <Box
          sx={{
            position: 'relative',
            bgcolor: isDark ? theme.palette.grey[800] : '#f5f5f5',
            color: isDark ? theme.palette.common.white : theme.palette.common.black,
            p: 2,
            pt: { xs: 5, sm: 2 },
            borderRadius: 2,
            overflowX: 'auto',
            fontSize: SCRIPT_CONFIG.fontSize,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={handleCopyScript}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              fontSize: '0.7rem',
              px: 1.5,
              py: 0.25,
              textTransform: 'uppercase',
            }}
          >
            {t('COPY')}
          </Button>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <code>{codeString}</code>
          </pre>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: { xs: '100%', md: '90%' } }}>
        <Button onClick={redirectToRoot} variant="contained">
          {t('FINISH')}
        </Button>
      </Box>
    </Box>
  );
};
